import { useState, useEffect } from "react";
import { Users, ArrowsLeftRight, Wallet } from "phosphor-react";

import {
  fetchDashboardStats,
  fetchRecentWalletTransactions,
  fetchMonthlyUserStats,
  fetchMonthlyTransactions,
  fetchTransactionTypeBreakdown,
} from "../services/api";

import UserGrowthChart from "./charts/UserGrowthChart";
import CreditDebitChart from "./charts/CreditDebitChart";
import TransactionTypeChart from "./charts/TransactionTypeChart";

// ✅ IMPORT SAME TRANSACTIONS PAGE
import Transactions from "./Transactions";

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_users: 0,
    total_transactions: 0,
    total_balance: 0,
  });

  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState([]);
  const [transactionStats, setTransactionStats] = useState([]);
  const [transactionTypeStats, setTransactionTypeStats] = useState([]);

  const rootStyle = getComputedStyle(document.documentElement);
  const themeColor = `rgb(${rootStyle.getPropertyValue("--theme-color").trim()})`;
  const textColor = `rgb(${rootStyle.getPropertyValue("--theme-text-color").trim()})`;

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const [statsData, usersData, txData, typeData] = await Promise.all([
          fetchDashboardStats(),
          fetchMonthlyUserStats(),
          fetchMonthlyTransactions(),
          fetchTransactionTypeBreakdown(),
        ]);

        setStats(statsData);

        const monthNames = [
          "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December",
        ];

        const formattedUsers = usersData
          .map((item) => {
            const index = item.month.includes("-")
              ? parseInt(item.month.split("-")[1]) - 1
              : parseInt(item.month) - 1;

            return {
              month: monthNames[index] || item.month,
              users: Number(item.users) || 0,
            };
          })
          .sort(
            (a, b) => monthNames.indexOf(a.month) - monthNames.indexOf(b.month)
          );

        setUserStats(formattedUsers);
        setTransactionStats(txData);
        setTransactionTypeStats(typeData);
      } catch (error) {
        console.error("Dashboard load error:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  return (
    <div className="container-fluid px-0">

      {/* ================= DASHBOARD STATS ================= */}
      <div className="row">
        <div className="col-md-12">
          <div className="dashborad-grid">

            {/* TOTAL USERS */}
            <div className="sm-box-big">
              <div className="theme-card-white h-100 dash-card">
                <div className="move-inner">
                  <div className="dash-content-top">
                    <p className="dash-card-text">
                      {loading ? "Loading..." : stats.total_users.toLocaleString()}
                    </p>
                    <div className="dash-card-icon"><Users /></div>
                  </div>
                  <div className="dash-card-content">
                    <h5 className="text-xxl font-600 theme-text-black mb-1">
                      Total Users
                    </h5>
                    <small className="text-sm font-400 theme-text-black">
                      Total registered users on platform
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* TOTAL TRANSACTIONS */}
            <div className="sm-box-big">
              <div className="theme-card-white h-100 dash-card">
                <div className="move-inner">
                  <div className="dash-content-top">
                    <p className="dash-card-text">
                      {loading
                        ? "Loading..."
                        : stats.total_transactions.toLocaleString()}
                    </p>
                    <div className="dash-card-icon"><ArrowsLeftRight /></div>
                  </div>
                  <div className="dash-card-content">
                    <h5 className="text-xxl font-600 theme-text-black mb-1">
                      Total Transactions
                    </h5>
                    <small className="text-sm font-400 theme-text-black">
                      All successful wallet transactions
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* TOTAL BALANCE */}
            <div className="sm-box-big">
              <div className="theme-card-white h-100 dash-card">
                <div className="move-inner">
                  <div className="dash-content-top">
                    <p className="dash-card-text">
                      {loading
                        ? "Loading..."
                        : `$${Number(stats.total_balance).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}`}
                    </p>
                    <div className="dash-card-icon"><Wallet /></div>
                  </div>
                  <div className="dash-card-content">
                    <h5 className="text-xxl font-600 theme-text-black mb-1">
                      Total Balance
                    </h5>
                    <small className="text-sm font-400 theme-text-black">
                      Combined wallet balance of all users
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* USER GROWTH */}
            <div className="lg-box-big">
              <div className="theme-card-white h-100 dash-growth">
                <div className="theme-card-header">
                  <h5 className="card-head">Monthly User Growth</h5>
                </div>
                <UserGrowthChart
                  userStats={userStats}
                  themeColor={themeColor}
                  textColor={textColor}
                />
              </div>
            </div>

            {/* TRANSACTION CHARTS */}
            <div className="sm-box-big">
              <div className="vertical-chart h-100">
                <div className="theme-card-white h-100">
                  <div className="theme-card-header">
                    <h5 className="card-head">Credit vs Debit Overview</h5>
                  </div>
                  <CreditDebitChart
                    transactionStats={transactionStats}
                    themeColor={themeColor}
                    textColor={textColor}
                  />
                </div>

                <div className="theme-card-white h-100">
                  <div className="theme-card-header">
                    <h5 className="card-head">Transaction Type Breakdown</h5>
                  </div>
                  <TransactionTypeChart
                    transactionTypeStats={transactionTypeStats}
                    themeColor={themeColor}
                    textColor={textColor}
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ================= FULL TRANSACTIONS TABLE ================= */}
      <div className="row mt-4">
        <div className="col-12">
          <Transactions />
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
